// pages/formularios/TesteFormulario.js — "Enviar teste" do rascunho.
//
// Out/26, Formulários PR 2. Publicado não muda (a Meta não deixa), então o
// teste é o único jeito de ver o formulário no celular antes de travar. Vai
// em modo rascunho da Meta para um número que escreveu pro chip nas últimas
// 24h (mensagem interativa só sai com a janela aberta). A resposta do teste
// aparece aqui — enquanto o diálogo está aberto, ele pergunta ao servidor a
// cada poucos segundos — e NÃO vai pro CRM: o rodapé mostra o que iria.

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Typography,
} from '@mui/material';
import { FiCheckCircle, FiSmartphone } from 'react-icons/fi';
import api from '../../services/Api';
import RespostasLista from './RespostasLista';
import { quando } from './ListaFormularios';
import { SYNE, ajuda, botaoPrincipal, botaoTexto, campo, dialogoPapel } from './estilo';

const CHAVE = 'cetto:formulario-teste-numero';
const CORES = {
  texto: 'var(--fm-ink)', texto2: 'var(--fm-label)', texto3: 'var(--fm-cap)', borda: 'var(--fm-bdr)',
  fundo: 'var(--fm-bg)', verde: 'var(--fm-green-t)', ambar: 'var(--fm-amber-t)',
};
const PERGUNTAR_A_CADA = 4000;
const DESISTIR_DEPOIS = 10 * 60 * 1000;

function erroDaApi(err, padrao) {
  const d = err?.response?.data?.detail;
  if (typeof d === 'string') return d;
  return d?.mensagem || padrao;
}

/**
 * @param salvar   () => Promise<{id}|null> — grava o rascunho antes de testar
 * @param onProblemas (lista) — a Meta recusou o JSON: o construtor marca os blocos
 */
export default function TesteFormulario({ open, onClose, formId, nome, chipNome, salvar, onProblemas }) {
  const [numero, setNumero] = useState(() => { try { return localStorage.getItem(CHAVE) || ''; } catch { return ''; } });
  const [texto, setTexto] = useState('');
  const [cta, setCta] = useState('Abrir');
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState('');
  const [teste, setTeste] = useState(null);
  const desde = useRef(0);

  useEffect(() => {
    if (!open) return;
    setErro('');
    setTexto((t) => t || `Teste do formulário “${nome || 'sem nome'}”. Toque no botão para abrir.`);
  }, [open, nome]);

  const ler = useCallback(async () => {
    if (!formId) return null;
    try {
      const r = await api.get(`/formularios/${formId}/teste`);
      setTeste(r.data?.teste || null);
      return r.data?.teste || null;
    } catch {
      return null;
    }
  }, [formId]);

  useEffect(() => { if (open) ler(); }, [open, ler]);

  // Pergunta enquanto o diálogo está aberto e o teste não foi respondido.
  useEffect(() => {
    if (!open || !teste || teste.respondido_em) return undefined;
    const t = setInterval(() => {
      if (Date.now() - desde.current > DESISTIR_DEPOIS) { clearInterval(t); return; }
      if (document.visibilityState !== 'hidden') ler();
    }, PERGUNTAR_A_CADA);
    return () => clearInterval(t);
  }, [open, teste, ler]);

  const enviar = async () => {
    setEnviando(true); setErro('');
    try {
      const salvo = await salvar();
      if (!salvo) return;
      const r = await api.post(`/formularios/${salvo.id}/testar`, { telefone: numero, texto, cta });
      try { localStorage.setItem(CHAVE, numero); } catch { /* sem storage */ }
      desde.current = Date.now();
      setTeste(r.data);
    } catch (err) {
      const lista = err?.response?.data?.detail?.problemas;
      if (Array.isArray(lista)) { onProblemas?.(lista); onClose(); return; }
      setErro(erroDaApi(err, 'Não foi possível enviar o teste.'));
    } finally {
      setEnviando(false);
    }
  };

  const aguardando = teste && !teste.respondido_em;

  return (
    <Dialog open={open} onClose={enviando ? undefined : onClose} maxWidth="sm" fullWidth PaperProps={dialogoPapel} aria-labelledby="teste-formulario-titulo">
      <DialogTitle id="teste-formulario-titulo" sx={{ fontFamily: SYNE, fontWeight: 800, pb: 1 }}>Enviar teste</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Typography sx={{ fontSize: '0.86rem', color: 'var(--fm-body)', lineHeight: 1.5 }}>
          O rascunho vai para o seu celular como o cliente vai receber. A Meta só entrega para quem mandou mensagem
          {chipNome ? <> para o chip <b style={{ color: 'var(--fm-title)' }}>{chipNome}</b></> : ' para o chip'} nas últimas 24 horas — se for preciso, mande um “oi” antes. A resposta do teste não vai para o CRM.
        </Typography>
        <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' } }}>
          <TextField
            size="small" label="Seu número" value={numero} onChange={(e) => setNumero(e.target.value)}
            placeholder="21 98888-7777" inputProps={{ inputMode: 'tel' }} sx={campo} fullWidth
          />
          <TextField size="small" label="Texto do botão" value={cta} onChange={(e) => setCta(e.target.value)} inputProps={{ maxLength: 20 }} sx={campo} fullWidth />
        </Box>
        <TextField size="small" label="Mensagem" value={texto} onChange={(e) => setTexto(e.target.value)} multiline minRows={2} sx={campo} fullWidth />
        {erro && <Alert severity="error" sx={{ borderRadius: 2 }}>{erro}</Alert>}

        {teste && (
          <Box sx={{ border: '1px solid var(--fm-bdr)', borderRadius: '12px', p: 2, bgcolor: 'var(--fm-bg)' }} aria-live="polite">
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: teste.respondido_em ? 1.5 : 0 }}>
              {aguardando
                ? <CircularProgress size={16} sx={{ color: 'var(--fm-green)' }} />
                : <FiCheckCircle size={16} color="var(--fm-green-t)" />}
              <Typography sx={{ fontSize: '0.84rem', color: 'var(--fm-title)', fontWeight: 600 }}>
                {aguardando
                  ? `Teste enviado para o número terminado em ${(teste.para || '').replace(/\D/g, '') || '—'} ${quando(teste.enviado_em)}. Responda no celular — a resposta aparece aqui.`
                  : `Resposta do teste ${quando(teste.respondido_em)}`}
              </Typography>
            </Box>
            {teste.respondido_em && <RespostasLista respostas={teste.respostas} crm={teste.crm} teste cores={CORES} />}
          </Box>
        )}
        {!teste && <Typography sx={ajuda}><FiSmartphone size={12} style={{ verticalAlign: '-2px' }} /> Nenhum teste deste formulário ainda.</Typography>}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={enviando} sx={{ ...botaoTexto, color: 'var(--fm-body)' }}>Fechar</Button>
        <Button
          variant="contained" disableElevation onClick={enviar} disabled={enviando || numero.replace(/\D/g, '').length < 10 || !texto.trim() || !cta.trim()}
          startIcon={enviando ? <CircularProgress size={14} sx={{ color: 'inherit' }} /> : <FiSmartphone size={14} />}
          sx={{ ...botaoPrincipal, px: 2.5 }}
        >
          {enviando ? 'Enviando teste…' : teste ? 'Enviar outro teste' : 'Enviar teste'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
